import { Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { Router } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import App from './App'
import './i18n'
import { UserProvider } from 'context/authentication/UserContext'
import { getEnv, isSentryEnabled } from 'global/helpers/environment'
import { appHistory } from 'global/helpers/history'
import { ServerError } from 'global/helpers/requestError'
import * as Sentry from '@sentry/react'
import { setWorkerUrl } from 'maplibre-gl'
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'

setWorkerUrl(maplibreWorkerUrl)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000,
      retry: (failureCount, error) => {
        if (error instanceof ServerError) {
          const { status } = error.response
          // do not retry any 4xx statuses, except 408 Request Timeout and 429 Too Many Requests
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            return false
          }
        }
        return failureCount < 3
      },
    },
  },
})

if (isSentryEnabled()) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: getEnv(),
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({ history: appHistory }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  })
}

const container = document.getElementById('root')
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!)
root.render(
  <QueryClientProvider client={queryClient}>
    <UserProvider>
      <Suspense fallback="Loading ...">
        <Router history={appHistory}>
          <App />
        </Router>
      </Suspense>{' '}
    </UserProvider>
  </QueryClientProvider>
)
